// client/src/components/uploadtools/ImportMapModal.js
import React, { useState, useEffect, useMemo } from 'react';

function ImportMapModal({ onClose, user }) {
  const [jsonFile, setJsonFile] = useState(null);
  const [mapName, setMapName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [jsonData, setJsonData] = useState(null);
  const [detectedOrigin, setDetectedOrigin] = useState(null);
  const [confirmedOrigin, setConfirmedOrigin] = useState(null);

  const [selectedGroupKey, setSelectedGroupKey] = useState(null);

  const [rightsConfirmed, setRightsConfirmed] = useState(false);

  const tryParseJSON = (text) => {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    async function loadAndDetect() {
      setError(null);
      setJsonData(null);
      setDetectedOrigin(null);
      setConfirmedOrigin(null);
      setSelectedGroupKey(null);
      if (!jsonFile) return;

      try {
        const rawText = await jsonFile.text();
        const parsed = tryParseJSON(rawText);
        if (!parsed) throw new Error('Invalid JSON data.');

        setJsonData(parsed);
        setMapName(parsed.name || parsed.title || '');

        if (parsed.crs && parsed.crs.topLeft && parsed.crs.bottomRight && parsed.crs.order) {
          const tl = parsed.crs.topLeft;
          const br = parsed.crs.bottomRight;
          const order = parsed.crs.order.toLowerCase();

          let originGuess = null;

          const flipX = tl[0] > br[0];
          const flipY = tl[1] > br[1];
          const swapped = order === 'yx';

          if (!swapped) {
            if (!flipX && !flipY) originGuess = 'bottom_left';
            else if (flipX && !flipY) originGuess = 'bottom_right';
            else if (!flipX && flipY) originGuess = 'top_left';
            else if (flipX && flipY) originGuess = 'top_right';
          } else {
            if (!flipX && !flipY) originGuess = 'yx_bottom_left';
            else if (flipX && !flipY) originGuess = 'yx_bottom_right';
            else if (!flipX && flipY) originGuess = 'yx_top_left';
            else if (flipX && flipY) originGuess = 'yx_top_right';
          }

          setDetectedOrigin(originGuess);
          setConfirmedOrigin(originGuess);
        }
      } catch (err) {
        setError(err.message || 'Failed to load or parse JSON.');
        setJsonData(null);
        setDetectedOrigin(null);
        setConfirmedOrigin(null);
        setSelectedGroupKey(null);
      }
    }

    loadAndDetect();
  }, [jsonFile]);

  const originOptions = [
    { value: 'bottom_left', label: 'Bottom Left (default)' },
    { value: 'bottom_right', label: 'Bottom Right' },
    { value: 'top_left', label: 'Top Left' },
    { value: 'top_right', label: 'Top Right' },
    { value: 'yx_bottom_left', label: 'YX Bottom Left (swapped axes)' },
    { value: 'yx_bottom_right', label: 'YX Bottom Right (swapped axes)' },
    { value: 'yx_top_left', label: 'YX Top Left (swapped axes)' },
    { value: 'yx_top_right', label: 'YX Top Right (swapped axes)' }
  ];

  const getTransformCoords = (mapWidth, mapHeight, origin) => {
    return (x, y) => {
      let nx = x;
      let ny = y;
      const swapped = origin.startsWith('yx');
      const flipX = origin.includes('right');
      const flipY = origin.includes('top');

      if (swapped) [nx, ny] = [ny, nx];
      if (flipX) nx = mapWidth - nx;
      if (flipY) ny = mapHeight - ny;

      return [nx, ny];
    };
  };

  const inferredExtents = useMemo(() => {
    if (!jsonData) return null;

    let maxX = 0;
    let maxY = 0;
    let foundAny = false;

    const scanPins = (pins) => {
      if (!Array.isArray(pins)) return;
      for (const pin of pins) {
        let x;
        let y;
        if (pin && typeof pin === 'object' && !Array.isArray(pin)) {
          x = pin.x;
          y = pin.y;
        } else if (Array.isArray(pin)) {
          x = pin[0];
          y = pin[1];
        }
        if (typeof x === 'number' && typeof y === 'number') {
          foundAny = true;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    };

    if (jsonData.groups && typeof jsonData.groups === 'object') {
      for (const group of Object.values(jsonData.groups)) {
        scanPins(group.pins || group.locations || []);
      }
    }

    if (jsonData.query && jsonData.query.markers && typeof jsonData.query.markers === 'object') {
      for (const pins of Object.values(jsonData.query.markers)) {
        scanPins(pins);
      }
    }

    if (jsonData.markers && typeof jsonData.markers === 'object') {
      for (const pins of Object.values(jsonData.markers)) {
        scanPins(pins);
      }
    }

    if (!foundAny) return null;
    return { width: maxX, height: maxY };
  }, [jsonData]);

  let mapWidth =
    jsonData?.crs && jsonData.crs.topLeft && jsonData.crs.bottomRight
      ? Math.abs(jsonData.crs.bottomRight[0] - jsonData.crs.topLeft[0])
      : jsonData?.width || 2048;

  let mapHeight =
    jsonData?.crs && jsonData.crs.topLeft && jsonData.crs.bottomRight
      ? Math.abs(jsonData.crs.bottomRight[1] - jsonData.crs.topLeft[1])
      : jsonData?.height || 2048;

  if (
    (!jsonData?.crs || !jsonData.crs.topLeft || !jsonData.crs.bottomRight) &&
    !jsonData?.width &&
    !jsonData?.height &&
    inferredExtents
  ) {
    mapWidth = inferredExtents.width;
    mapHeight = inferredExtents.height;
  }

  const transformCoords = getTransformCoords(mapWidth, mapHeight, confirmedOrigin || 'bottom_left');

  const groupEntries = useMemo(() => {
    if (!jsonData) return [];

    if (jsonData.groups) {
      return Object.entries(jsonData.groups).filter(([key, group]) => group);
    }

    if (jsonData.query && jsonData.query.markers && typeof jsonData.query.markers === 'object') {
      return Object.entries(jsonData.query.markers).map(([key, pins]) => [
        key,
        { name: key, pins }
      ]);
    }

    if (jsonData.markers && typeof jsonData.markers === 'object') {
      return Object.entries(jsonData.markers).map(([key, pins]) => [
        key,
        { name: key, pins }
      ]);
    }

    return [];
  }, [jsonData]);

  const previewPins = useMemo(() => {
    if (!jsonData || !selectedGroupKey) return [];
    let pins = [];

    if (jsonData.groups && jsonData.groups[selectedGroupKey]) {
      const group = jsonData.groups[selectedGroupKey];
      pins =
        (jsonData.markers && jsonData.markers[selectedGroupKey]) ||
        group.pins ||
        group.locations ||
        [];
    } else if (jsonData.query && jsonData.query.markers && jsonData.query.markers[selectedGroupKey]) {
      pins = jsonData.query.markers[selectedGroupKey];
    } else if (jsonData.markers && jsonData.markers[selectedGroupKey]) {
      pins = jsonData.markers[selectedGroupKey];
    }

    if (!Array.isArray(pins)) return [];
    return pins;
  }, [jsonData, selectedGroupKey]);

  useEffect(() => {
    if (groupEntries.length > 0) {
      setSelectedGroupKey(groupEntries[0][0]);
    } else {
      setSelectedGroupKey(null);
    }
  }, [groupEntries]);

  return (
    <div
      className="map-modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.78)',
        zIndex: 1000
      }}
    >
      <div
        className="map-modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#18192a',
          color: '#fff',
          padding: '32px 28px 20px',
          borderRadius: '14px',
          maxWidth: 420,
          margin: '60px auto',
          boxShadow: '0 6px 32px #000b, 0 0 0 2px #23272b',
          position: 'relative',
          fontFamily: 'inherit',
          userSelect: 'none'
        }}
      >
        <h2
          style={{
            fontSize: '1.25em',
            fontWeight: 'bold',
            marginBottom: 16,
            color: '#fff',
            textAlign: 'center',
            letterSpacing: '0.02em'
          }}
        >
          Import Map from Wiki.gg DataMaps JSON
        </h2>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setSuccess(null);

            if (!jsonData) {
              setError('No valid JSON loaded.');
              return;
            }
            if (!confirmedOrigin) {
              setError('Please confirm the map origin.');
              return;
            }
            if (!mapName || !mapName.trim()) {
              setError('Please enter a map name.');
              return;
            }
            if (!rightsConfirmed) {
              setError('Please confirm that you have the rights to import this data.');
              return;
            }

            setLoading(true);

            try {
              let backgroundImg = imageUrl;
              let mapId = null;

              if (imageFile) {
                const mapForm = new FormData();
                mapForm.append('map', imageFile);
                mapForm.append('game_name', mapName);
                mapForm.append('display_name', mapName);
                mapForm.append('uploader_id', user?.id || 1);

                const imgRes = await fetch('/api/upload-map', {
                  method: 'POST',
                  body: mapForm,
                  credentials: 'include'
                });
                const imgData = await imgRes.json();
                if (!imgRes.ok || !imgData.success || !imgData.id) throw new Error('Image upload failed');

                mapId = imgData.id;

                const infoRes = await fetch(`/api/maps/${imgData.id}`);
                const infoData = await infoRes.json();
                if (!infoRes.ok || !infoData.image_url) throw new Error('Could not retrieve uploaded image URL.');
                backgroundImg = infoData.image_url;
              }

              const res = await fetch('/api/import-wikigg-map', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  json: jsonData,
                  imageUrl: backgroundImg,
                  uploaderId: user?.id || null,
                  mapId: mapId,
                  confirmedOrigin: confirmedOrigin,
                  displayName: mapName
                })
              });
              const data = await res.json();
              if (!res.ok) throw new Error(data.error || 'Import failed.');

              setSuccess('Map imported successfully!');
              setTimeout(() => {
                onClose();
                if (data.mapUrl) window.location.href = data.mapUrl;
              }, 1300);
            } catch (err) {
              setError(err.message || 'Import failed.');
            } finally {
              setLoading(false);
            }
          }}
        >
          <label style={{ fontWeight: 600, display: 'block', marginTop: 12, marginBottom: 7 }}>
            DataMaps JSON file:
          </label>
          <input
            type="file"
            accept=".json,application/json"
            style={{
              background: 'none',
              color: '#49f',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '8px 0',
              marginBottom: 12
            }}
            onChange={(e) => {
              setJsonFile(e.target.files[0]);
            }}
          />

          <label style={{ fontWeight: 600, display: 'block', marginTop: 14, marginBottom: 6 }}>
            Map Name:
          </label>
          <input
            type="text"
            placeholder="Enter map name"
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '9px 11px',
              marginBottom: 12,
              width: '100%'
            }}
          />

          <label style={{ fontWeight: 600, display: 'block', marginTop: 14, marginBottom: 6 }}>
            Map Image URL:
          </label>
          <input
            type="url"
            className="map-modal-input"
            placeholder="Paste map background image URL"
            value={imageUrl}
            style={{
              background: '#23272b',
              color: '#fff',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '9px 11px',
              marginBottom: 8
            }}
            onChange={(e) => setImageUrl(e.target.value)}
          />

          <div style={{ textAlign: 'center', margin: '6px 0 8px 0', fontSize: '1em' }}>or</div>
          <input
            type="file"
            accept="image/*"
            style={{
              background: 'none',
              color: '#49f',
              border: '1px solid #313639',
              borderRadius: 5,
              fontSize: '1em',
              padding: '8px 0',
              marginBottom: 10
            }}
            onChange={(e) => setImageFile(e.target.files[0])}
          />

          {jsonData && (
            <div
              style={{
                background: '#222839',
                padding: 12,
                borderRadius: 8,
                marginTop: 12,
                maxHeight: 320,
                overflowY: 'auto',
                fontSize: '0.9em',
                color: '#ccc',
                userSelect: 'text'
              }}
            >
              <div>
                <strong>Map Title:</strong> {jsonData.name || jsonData.title || 'N/A'}
              </div>
              <div>
                <strong>Game:</strong> {jsonData.game || 'N/A'}
              </div>
              <div>
                <strong>Detected Origin:</strong> {detectedOrigin || 'Unknown'}
              </div>

              <label style={{ marginTop: 10, display: 'block', fontWeight: 600 }}>
                Confirm or select map origin:
              </label>
              <select
                value={confirmedOrigin || ''}
                onChange={(e) => setConfirmedOrigin(e.target.value)}
                style={{
                  background: '#23272b',
                  color: '#fff',
                  border: '1px solid #313639',
                  borderRadius: 5,
                  fontSize: '1em',
                  padding: '6px 8px',
                  width: '100%'
                }}
              >
                <option value="" disabled>
                  Select map origin
                </option>
                {originOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {groupEntries.length > 0 && (
                <>
                  <label style={{ marginTop: 10, display: 'block', fontWeight: 600 }}>
                    Preview POI Group:
                  </label>
                  <select
                    value={selectedGroupKey || ''}
                    onChange={(e) => setSelectedGroupKey(e.target.value)}
                    style={{
                      background: '#23272b',
                      color: '#fff',
                      border: '1px solid #313639',
                      borderRadius: 5,
                      fontSize: '1em',
                      padding: '6px 8px',
                      width: '100%'
                    }}
                  >
                    {groupEntries.map(([key, group]) => (
                      <option key={key} value={key}>
                        {group.name || key}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {(imageUrl || imageFile) && (
                <div
                  style={{
                    marginTop: 12,
                    position: 'relative',
                    width: '100%',
                    height: 150,
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : imageUrl}
                    alt="Map Preview"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      borderRadius: 8,
                      border: '1px solid #444'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: 150,
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                  >
                    {previewPins.map((pin, idx) => {
                      const xRaw =
                        pin.x !== undefined
                          ? pin.x
                          : Array.isArray(pin) && pin[0] !== undefined
                          ? pin[0]
                          : 0;
                      const yRaw =
                        pin.y !== undefined
                          ? pin.y
                          : Array.isArray(pin) && pin[1] !== undefined
                          ? pin[1]
                          : 0;
                      const [x, y] = transformCoords(xRaw, yRaw);

                      const previewHeight = 150;
                      const scale = previewHeight / mapHeight;
                      const left = x * scale;
                      const top = previewHeight - y * scale;

                      return (
                        <div
                          key={idx}
                          title={pin.label || pin.name || 'POI'}
                          style={{
                            position: 'absolute',
                            width: 8,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.85)',
                            left,
                            top,
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'auto',
                            border: '1px solid #333'
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div
            style={{
              marginTop: 14,
              padding: '10px 11px',
              borderRadius: 8,
              background: '#201f2e',
              border: '1px solid #343547',
              fontSize: '0.8em',
              color: '#c4cbf5',
              lineHeight: 1.4
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Legal notice</div>
            <div>
              Only import data that you own or have explicit permission to use. If this JSON comes from
              Wiki.gg, Fandom, or any other community wiki, you must be the original contributor/mapper or
              otherwise comply with their licence (for example, CC BY-SA, including attribution and
              share-alike). By importing, you accept that you are solely responsible for any licensing or
              attribution obligations and that Point Atlas is acting only as a mapping tool and does not
              provide or host Wiki.gg data.
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', marginTop: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={rightsConfirmed}
                onChange={(e) => setRightsConfirmed(e.target.checked)}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <span>
                I confirm that I have the right to import this data and will comply with any applicable
                licence (including attribution and share-alike where required).
              </span>
            </label>
          </div>

          {error && (
            <div
              style={{
                color: '#ff6868',
                fontWeight: 'bold',
                marginTop: 10,
                marginBottom: 4,
                textAlign: 'center'
              }}
            >
              {error}
            </div>
          )}

          {success && (
            <div
              style={{
                color: '#5dffa8',
                fontWeight: 'bold',
                marginTop: 10,
                marginBottom: 4,
                textAlign: 'center'
              }}
            >
              {success}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#23272b',
                color: '#fff',
                border: '1px solid #495158',
                borderRadius: 6,
                padding: '8px 18px',
                fontSize: '1em',
                fontWeight: 'bold',
                marginLeft: 10,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !confirmedOrigin || !mapName.trim() || !rightsConfirmed}
              style={{
                background: '#5374e7',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 18px',
                fontSize: '1em',
                fontWeight: 'bold',
                marginLeft: 10,
                opacity: loading || !confirmedOrigin || !mapName.trim() || !rightsConfirmed ? 0.7 : 1,
                cursor:
                  loading || !confirmedOrigin || !mapName.trim() || !rightsConfirmed
                    ? 'not-allowed'
                    : 'pointer'
              }}
            >
              {loading ? 'Importing...' : 'Import Map'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ImportMapModal;
